import { createFileRoute } from "@tanstack/react-router";
import { byCategory, products } from "@/data/products";
import { faqs } from "@/data/content";
import { Breadcrumbs, PageHeader, Section, SectionHeading } from "@/components/site/section";
import { CtaLink } from "@/components/site/cta";
import { FilterableGrid, ProductGrid, type FilterDef } from "@/components/site/product-grid";
import { MaterialExplorer } from "@/components/site/material-explorer";
import { FAQAccordion } from "@/components/site/faq-accordion";
import { FinalCTA } from "@/components/site/final-cta";
import { pageUrl } from "@/data/site";

export const Route = createFileRoute("/custom-pens")({
  head: () => ({
    meta: [
      { title: "Pens and Pencils in Arkansas | Paul's Pens n Things" },
      {
        name: "description",
        content:
          "Handcrafted fountain, rollerball, bolt-action, rifle cartridge, cigar-style, pencil and pen sets, turned to order in Paragould, Arkansas.",
      },
      { property: "og:title", content: "Pens and pencils, handcrafted in Arkansas" },
      {
        property: "og:description",
        content: "Fountain, rollerball, bolt-action, cartridge, pencil and pen sets made around your materials and occasion.",
      },
      { property: "og:url", content: pageUrl("/custom-pens") },
    ],
    links: [{ rel: "canonical", href: pageUrl("/custom-pens") }],
  }),
  component: CustomPens,
});

const penFilters: FilterDef[] = [
  { id: "fountain", label: "Fountain & rollerball", match: (p) => p.subcategory === "Fountain & Rollerball" },
  { id: "bolt", label: "Bolt-action", match: (p) => p.tags.includes("bolt-action") },
  { id: "cartridge", label: "Rifle cartridge", match: (p) => p.tags.includes("rifle cartridge") },
  { id: "designer", label: "Designer", match: (p) => p.tags.includes("designer") },
  { id: "cigar", label: "Cigar style", match: (p) => p.subcategory === "Cigar Style" },
  { id: "sets", label: "Pen & pencil sets", match: (p) => p.tags.includes("set") },
  { id: "wood", label: "Wood", match: (p) => p.tags.includes("wood") },
  { id: "resin", label: "Resin / acrylic", match: (p) => p.tags.includes("resin") },
  { id: "specialty", label: "Specialty material", match: (p) => p.tags.includes("specialty") },
  { id: "available", label: "Available", match: (p) => p.status === "available" },
  { id: "prev", label: "Previously created", match: (p) => p.status === "previously-created" },
];

const styles = [
  { name: "Fountain", body: "A nib and ink. The most personal way to write, and the most rewarding to own." },
  { name: "Rollerball", body: "Fountain-pen smoothness with a cartridge refill. A good first step for a gift." },
  { name: "Ballpoint", body: "The everyday workhorse. Reliable, easy to refill, comfortable in any pocket." },
  { name: "Bolt-action", body: "A small replica bolt extends the refill. Satisfying, and always a conversation." },
  { name: "Rifle cartridge", body: "Built around a genuine case in .30 or .50 caliber." },
  { name: "Cigar style", body: "A wider barrel through the middle for a relaxed, secure grip." },
  { name: "Designer", body: "Slimmer profiles with more decorative hardware, suited to fine materials." },
  { name: "Pencil & sets", body: "A matched pair turned from one board so the grain carries across both." },
];

function CustomPens() {
  const pens = byCategory("pens");
  const previous = pens.filter((p) => p.status === "previously-created");
  return (
    <>
      <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Pens and Pencils" }]} />
      <PageHeader
        eyebrow="Pens and pencils"
        title="A pen is a small object that gets carried for years."
        lede="Paul has been turning pens since 2006. Choose a style, choose a material, and he will build the rest around the person who will use it."
      >
        <CtaLink to="/design-your-piece" arrow className="h-12">
          Design Your Piece
        </CtaLink>
        <CtaLink to="#catalog" variant="outline" className="h-12">
          Browse the catalog
        </CtaLink>
      </PageHeader>

      <Section tone="paper" labelledBy="pen-catalog-heading" id="catalog">
        <div className="shell">
          <SectionHeading
            id="pen-catalog-heading"
            eyebrow="The catalog"
            title="Pens on and off the bench."
            lede="Filter by style, material or availability. Anything marked Previously Created can be remade in your own materials."
          />
          <FilterableGrid source={pens} filters={penFilters} context="custom_pens" />
        </div>
      </Section>

      <Section labelledBy="pen-styles-heading">
        <div className="shell">
          <SectionHeading
            id="pen-styles-heading"
            eyebrow="Pen style guide"
            title="Which style suits the person?"
            lede="If you are buying for someone else, the mechanism matters as much as the material. Here is the plain-English version."
          />
          <dl className="mt-12 grid gap-px border border-hairline bg-hairline sm:grid-cols-2 lg:grid-cols-4">
            {styles.map((s) => (
              <div key={s.name} className="bg-background p-6">
                <dt className="font-[family-name:var(--font-display)] text-xl">{s.name}</dt>
                <dd className="mt-2 text-sm leading-relaxed text-muted-foreground">{s.body}</dd>
              </div>
            ))}
          </dl>
        </div>
      </Section>

      <Section tone="paper" labelledBy="pen-materials-heading">
        <div className="shell">
          <SectionHeading
            id="pen-materials-heading"
            eyebrow="Materials"
            title="Start with the blank."
            lede="Every pen begins as a square of something. Ask Paul about current availability before you settle on one."
          />
          <MaterialExplorer />
        </div>
      </Section>

      <Section labelledBy="pen-custom-heading">
        <div className="shell grid gap-12 lg:grid-cols-2">
          <div>
            <SectionHeading
              id="pen-custom-heading"
              eyebrow="Customization"
              title="What can actually be changed?"
              lede="Almost everything, within the limits of the hardware."
            />
            <ul className="mt-8 space-y-4 text-sm leading-relaxed text-muted-foreground">
              <li className="border-t border-hairline pt-4">
                <strong className="text-ink">Material and color.</strong> Wood, resin, antler, fordite,
                cartridge components or a keepsake cast in clear resin.
              </li>
              <li className="border-t border-hairline pt-4">
                <strong className="text-ink">Hardware finish.</strong> Gold, chrome, gunmetal or black,
                depending on the pen kit chosen.
              </li>
              <li className="border-t border-hairline pt-4">
                <strong className="text-ink">Mechanism.</strong> Fountain nib, rollerball, twist or click
                ballpoint, bolt-action or pencil.
              </li>
              <li className="border-t border-hairline pt-4">
                <strong className="text-ink">Engraving.</strong> Names, dates and short messages, subject to the
                space available on the barrel.
              </li>
              <li className="border-t border-hairline pt-4">
                <strong className="text-ink">Presentation.</strong> Ask about cases and packaging when a pen is
                being given as a gift.
              </li>
            </ul>
          </div>
          <div className="surface-card p-8">
            <h2 className="display-md">How pricing works</h2>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              A pen&apos;s price comes from three things: the hardware kit, the material, and the hours. A
              straightforward wood ballpoint sits at one end; a fountain pen in stabilized burl with gold
              fittings sits at the other.
            </p>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              Paul quotes each piece individually once he knows what you want, and there is no obligation when
              he does. Payment happens only after you have agreed to the details, through a PayPal invoice.
            </p>
            <div className="mt-7">
              <CtaLink to="/design-your-piece" arrow className="h-12">
                Ask Paul for a price
              </CtaLink>
            </div>
          </div>
        </div>
      </Section>

      {previous.length > 0 && (
        <Section tone="paper" labelledBy="pen-prev-heading">
          <div className="shell">
            <SectionHeading
              id="pen-prev-heading"
              eyebrow="Previously created"
              title="Made before. Reimagined for you."
              lede="These pens have found their homes. Use them as a reference point for something entirely your own."
            />
            <ProductGrid products={previous} />
          </div>
        </Section>
      )}

      <Section labelledBy="pen-faq-heading">
        <div className="shell grid gap-10 lg:grid-cols-[0.8fr_1.2fr] lg:items-start">
          <SectionHeading id="pen-faq-heading" eyebrow="Questions" title="Pen questions, answered." />
          <FAQAccordion items={faqs.filter((f) => !f.pending).slice(0, 5)} />
        </div>
      </Section>

      <FinalCTA
        heading="Tell Paul about the pen you are imagining."
        body={`There are ${products.filter((p) => p.category === "pens").length} pens on this page and hundreds more that have already left the shop. Describe yours and Paul will tell you what is possible.`}
      />
    </>
  );
}
